import React, { useState, ReactNode, CSSProperties } from "react";
import { I18N } from "oc-I18N";
import ReactDOM from "react-dom";

/**
 * Dialogs ist eine Schnittstelle, über die das Framework Dialoge, z.B. für Fragen an den
 * User, oder für Fehlermeldungen, absetzen kann.
 * Damit die Dialoge angezeigt werden können, muss allerdings unbedingt die folgende
 * React-Komponente ins UI eingebunden werden: <DialogContainer/>
 * Ausserdem sollte import "oc-ui/dialogs/Dialogs.scss" erfolgen.
 * Das Styling oder den DialogContainer selbst kann man auch durch eine eigene Implementierung ersetzen -
 * man muss nur die dialogQueue verwenden sowie useAlertForDialogs=false setzen, am besten schaut man
 * sich den Code von DialogContainer an.
 */

interface IDialogButton {
  label: string;
  primary?: boolean;
  className?: string;
  action?: () => void;
}

interface IDialog {
  content?: ReactNode | string;
  buttonAndActionList?: IDialogButton[];
  title?: string | null;
  className?: string;
  severity?: number;
  showCloseButton?: boolean;
}

interface IShowToastOptions {
  type?: "info" | "warning" | "error";
  delay?: number;
  hideCloseButton?: boolean;
  style?: CSSProperties;
  onClose?: () => void;
}

export interface IWarningDetailsDialogButton {
  label: string;
  primary?: boolean;
  className?: string;
  action?: () => void;
}

export interface IWarningDetailsDialog {
  title?: string;
  text: string | ReactNode;
  imageUrl?: string;
  imageAlt?: string;
  buttons?: IWarningDetailsDialogButton[];
  showCloseButton?: boolean;
  showHideWarningCheckbox?: boolean;
  hideWarningCheckboxLabel?: string;
  onHideWarningCheckboxChange?: (checked: boolean) => void;
}

interface IAjaxError {
  status?: number;
  message?: string;
  responseText?: string;
  responseJSON?: {
    error?: {
      message?: string;
    };
  };
}

interface IStringWithSubstringBefore extends String {
  substringBefore(delimiter: string): string;
}

export let useAlertForDialogs = true;
export let forceUpdate: (value: Record<string, never>) => void = () => {};
export let dialogQueue: IDialog[] = [];
export const setDialogQueue = (newDialogQueue: IDialog[]): void => {
  dialogQueue = newDialogQueue.slice(-maxDialogQueueSize);
  forceUpdate({});
};
export const pushDialog = (newDialog: IDialog): void => {
  const updatedDialogQueue = dialogQueue
    .filter((a) => a?.severity && a.severity < 2)
    .concat(newDialog)
    .sort((a, b) => (b?.severity ?? 0) - (a?.severity ?? 0));

  setDialogQueue(updatedDialogQueue);
  APP?.trigger("showDialog");
};

export let maxDialogQueueSize = 3;

export function setUseAlertForDialogs(on: boolean): void {
  useAlertForDialogs = on;
}
export function setForceUpdate(update: (value: Record<string, never>) => void): void {
  forceUpdate = update;
}

/**
 * allgemeine Funktion, um einen Dialog anzuzeigen, also in die DialogQueue zu stellen
 * @param {jsx} content
 * @param {Array<label:string,primary:boolean,action:function>} buttonAndActionList, eine Liste von anzuzeigenden Buttons
 * @param {number} duration
 * @returns {Function}
 */
export function showDialog(dialog: IDialog = {}): () => void {
  const { content, buttonAndActionList = [], title = null, className = "", severity = 0 } = dialog;
  if (useAlertForDialogs) {
    // Falls (noch) kein DialogContainer existiert, wird ein normales JS-Alert verwendet
    if (_.isString(content)) {
      alert(content);
    } else {
      new Promise<HTMLElement>((resolve, reject) => {
        const dummy = document.createElement("div");
        ReactDOM.render(content as any, dummy, () => resolve(dummy.children[0] as HTMLElement));
      }).then((html) => alert(html.innerHTML));
    }
    return () => {};
  }
  pushDialog(dialog);
  return () => removeDialog(dialog);
}

/**
 * entfernt einen Dialog
 */
export function removeDialog(dialog: IDialog): void {
  setDialogQueue(_.without(dialogQueue, dialog));
}

function toContent(htmlText: string | ReactNode): ReactNode {
  if (_.isString(htmlText)) {
    return <span dangerouslySetInnerHTML={{ __html: htmlText }}></span>;
  }
  return htmlText;
}

/**
 * zeigt einen kleinen Hinweistext an, der nach ein paar Sekunden verschwindet
 * @param {string|jsx} htmlText - Die anzuzeigende Nachricht.
 * @param {Object} [options={}] - Optional konfigurierbare Optionen.
 * @param {String} [options.type="Info"] - Welcher MessageType? (z.B. Info, Warning, Error)
 * @param {number} [options.delay=12000] - Dauer in Millisekunden, wie lange die Nachricht angezeigt werden soll.
 * @param {boolean} [options.hideCloseButton=false] - Soll der Schließen-Button ausgeblendet werden?
 * @param {React.CSSProperties} [options.style] - Individuelle CSS-Styles für die Nachricht.
 * @param {function():void} [options.onClose] - Callback, der beim Timeout oder bei einem Schließen der Nachricht aufgerufen wird.
 * @returns {function(): void} Callback, welches den Toast vorzeitig beendet.
 */
export function showToast(
  htmlText: string | ReactNode,
  options: IShowToastOptions = {},
): () => void {
  if (APP?.messages) {
    const message = APP.messages.show(
      htmlText,
      options.type,
      options.delay,
      options.hideCloseButton,
      options.style,
      options.onClose,
    );
    return () => message && message.hide();
  }
  const remove = showDialog({
    content: toContent(htmlText),
    className: "oc-ui-dialog-toast",
    severity: 0,
  });
  _.delay(remove, options.delay ?? 12000);
  return remove;
}

/**
 * zeigt einen Dialog an, der mit ok bestätigt werden muss
 * @param {string|jsx} htmlText
 * @param {function} okFun Callback, welches bei Click auf ok aufgerufen wird
 */
export function showOk(htmlText: string | ReactNode, okFun: () => void = () => {}): () => void {
  return showDialog({
    content: toContent(htmlText),
    className: "oc-ui-dialog-ok",
    buttonAndActionList: [{ label: I18N("OK"), action: okFun }],
    severity: 1,
  });
}

export function showOkPromise(htmlText: string | ReactNode): Promise<void> {
  return new Promise((resolve) => {
    showDialog({
      content: toContent(htmlText),
      className: "oc-ui-dialog-ok",
      buttonAndActionList: [{ label: I18N("OK"), action: resolve }],
      severity: 1,
    });
  });
}

/**
 * zeigt einen Warnungs-Dialog an, der mit ok bestätigt werden muss
 * @param {string|jsx} htmlText
 */
export function showWarning(htmlText: string | ReactNode): () => void {
  return showDialog({
    content: toContent(htmlText),
    className: "oc-ui-dialog-warning",
    buttonAndActionList: [{ label: I18N("OK") }],
    severity: 2,
  });
}

/**
 * zeigt einen Error-Dialog an, der mit ok bestätigt werden muss
 * @param {string|jsx} htmlText
 */
export function showError(htmlText: string | ReactNode): () => void {
  return showDialog({
    content: toContent(htmlText),
    className: "oc-ui-dialog-error",
    buttonAndActionList: [{ label: I18N("OK") }],
    severity: 3,
  });
}

/**
 * zeigt einen Hinweis an, dass man einen Moment warten muss, und wird erst durch Aufruf des
 * zurückgegebenen Callbacks beendet.
 * @param {string|jsx} htmlText
 * @param {Array<label:string,primary:boolean,action:function>} buttonAndActionList, eine Liste von anzuzeigenden Buttons
 * @returns {function(): void} Callback, welches den Dialog beendet
 */
export function showWaiting(
  htmlText: string | ReactNode,
  buttonAndActionList?: IDialogButton[],
): () => void {
  return showDialog({
    content: toContent(htmlText),
    className: "oc-ui-dialog-waiting",
    buttonAndActionList,
  });
}

/**
 * zeigt eine Frage an
 * @param {string|jsx} htmlText
 * @param {Array<label:string,primary:boolean,action:function>} buttonAndActionList, eine Liste von anzuzeigenden Buttons
 */
export function askQuestion(
  htmlText: string | ReactNode,
  buttonAndActionList: IDialogButton[],
): () => void {
  return showDialog({
    content: toContent(htmlText),
    className: "oc-ui-dialog-question",
    buttonAndActionList,
  });
}

/**
 * zeigt einen Warnungs-Detaildialog mit optionalem Bild und bis zu zwei Aktionsbuttons
 * @param {Object} options - Konfiguration des Dialogs
 * @param {string} [options.title="Warnung"] - Titel des Dialogs
 * @param {string|jsx} options.text - Hauptinhalt/Hinweistext des Dialogs
 * @param {string} [options.imageUrl] - Optionale Bild-URL
 * @param {string} [options.imageAlt="Warnungsbild"] - Alternativtext für das optionale Bild
 * @param {Array<label:string,primary:boolean,className:string,action:function>} [options.buttons=[]] - Optionale Aktionsbuttons
 * @param {boolean} [options.showCloseButton=true] - Soll oben rechts ein Schließen-Button angezeigt werden?
 * @returns {function(): void} Callback, welches den Dialog beendet.
 */
export function showWarningDetails({
  title = I18N("Warnung"),
  text,
  imageUrl,
  imageAlt = I18N("Warnungsbild"),
  buttons = [],
  showCloseButton = true,
  showHideWarningCheckbox = false,
  hideWarningCheckboxLabel = I18N("warnung::Warnung nicht mehr anzeigen"),
  onHideWarningCheckboxChange,
}: IWarningDetailsDialog): () => void {
  const buttonAndActionList =
    buttons.length > 0 ? buttons : [{ label: I18N("Fertig"), primary: true }];

  return showDialog({
    title,
    className: "oc-ui-dialog-question oc-ui-dialog-warning-details",
    content: (
      <div className="warning-details-content">
        <div className="warning-details-text">{toContent(text)}</div>
        {showHideWarningCheckbox && (
          <label className="warning-details-hide-toggle">
            <input
              type="checkbox"
              onChange={(event) =>
                onHideWarningCheckboxChange?.((event.target as HTMLInputElement)?.checked)
              }
            />{" "}
            {hideWarningCheckboxLabel}
          </label>
        )}
        {imageUrl && (
          <img className="warning-details-image" src={imageUrl} alt={imageAlt} loading="lazy" />
        )}
      </div>
    ),
    buttonAndActionList,
    severity: 1,
    showCloseButton,
  });
}

/**
 * zeigt eine Frage an, als Promise
 * @param {string|jsx} htmlText
 * @param {Array<label:string,primary:boolean,action:function>} buttonAndActionList, eine Liste von anzuzeigenden Buttons
 */
export function askQuestionPromise(
  htmlText: string | ReactNode,
  buttonList: IDialogButton[],
): Promise<IDialogButton> {
  return new Promise((resolve) => {
    showDialog({
      content: toContent(htmlText),
      className: "oc-ui-dialog-question",
      buttonAndActionList: buttonList.map((button, index) => ({
        ...button,
        action: () => resolve(button),
      })),
    });
  });
}

/**
 * Zeigt beliebigen htmlText oder jsx als Dialog mit Ok und Abbrechen.
 * Der htmlText sollte ein input-Feld beinhalten.
 * Bei OK gibt das Promise den Wert des Input-Felds zurück, bei Abbrechen null
 * @param {string|jsx} htmlText
 * @param {Array<label:string,primary:boolean,action:function>} buttonAndActionList, eine Liste von anzuzeigenden Buttons
 */
export function askQuestionWithInputPromise(htmlText: string | ReactNode): Promise<string | null> {
  return new Promise((resolve) => {
    showDialog({
      content: toContent(htmlText),
      className: "oc-ui-dialog-question oc-ui-dialog-input",
      buttonAndActionList: [
        {
          label: I18N("OK"),
          primary: true,
          action: () =>
            resolve(
              (document.querySelector(".oc-ui-dialog-input input") as HTMLInputElement)?.value ??
                null,
            ),
        },
        { label: I18N("Abbrechen"), action: () => resolve(null) },
      ],
    });
  });
}

/**
 * zeigt eine Frage ok / cancel - Frage an
 * @param {string|jsx} htmlText
 * @param {string} okLabel
 * @param {function} okFun
 * @param {function} cancelFun
 */
export function askQuestionOkOrCancel(
  htmlText: string | ReactNode,
  okLabel: string = I18N("OK"),
  okFun: () => void = () => {},
  cancelFun: () => void = () => {},
): () => void {
  return askQuestion(htmlText, [
    { label: okLabel, primary: true, action: okFun },
    { label: I18N("Abbrechen"), action: cancelFun },
  ]);
}

// wenn man eine eigene Implementierung wünscht, dann schreibt und verwendet man einfach seine
// eigene Componente, welche in analoger Weise mit den exportierten dialogQueue und removeDialog
// arbeitet
export function DialogContainer(): JSX.Element | null {
  setUseAlertForDialogs(false);
  setForceUpdate(useState({})[1]);
  const dialog = _.first(dialogQueue);
  if (dialog == null) {
    return null;
  }
  const { title, className, content, buttonAndActionList, showCloseButton = false } = dialog;
  return (
    <div className={`oc-ui-dialog ${className ?? ""}`}>
      <div className="dialog">
        <div className="title">{title}</div>
        {showCloseButton && (
          <button
            className="icon-close"
            aria-label={I18N("Schließen")}
            onClick={() => removeDialog(dialog)}
          ></button>
        )}
        <div className="text">{content}</div>
        {(buttonAndActionList?.length ?? 0) > 0 && (
          <div className="button-container">
            {buttonAndActionList!.map(({ label, primary, className, action = () => {} }) => (
              <button
                key={label}
                className={primary ? "primary-button" : className ?? "outlined-button"}
                onClick={() => {
                  removeDialog(dialog);
                  action();
                }}
                autoFocus={true}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * formattiert einen Ajax error als String
 * @param {Ajax Error} err
 * @returns {string|null}
 */
export function formatAjaxError(err: IAjaxError | null): string | null {
  if (err == null) {
    return null;
  }
  if (err.responseJSON) {
    return "(" + err.status + ") " + err.responseJSON?.error?.message;
  }
  let message = "";
  if (err.status != null) {
    message += "(" + err.status + ") ";
  }
  if (err.message != null) {
    message += err.message + " ";
  }
  if (err.responseText != null) {
    message += err.responseText;
  }
  return (message as unknown as IStringWithSubstringBefore).substringBefore("Stacktrace:");
}

function removeStacktrace(message: string | null): string | null {
  if (message) {
    message = (message as unknown as IStringWithSubstringBefore).substringBefore("Stacktrace:");
  }
  return message;
}

/**
 * zeigt einen serverCommunicationError als Dialog an
 * @param {Ajax Error} err
 */
export function serverCommunicationError(err: IAjaxError): void {
  console.log("serverCommunicationError", err);
  showInternalError(removeStacktrace(formatAjaxError(err)));
}

/**
 * zeigt eine serverCommunicationWarning als Dialog an
 * @param {Ajax Error} err
 */
export function serverCommunicationWarning(err: IAjaxError): void {
  console.log("serverCommunicationError", err);
  showInternalWarning(removeStacktrace(formatAjaxError(err)));
}

/**
 * Fügt ein catch zu einem Promise hinzu - aber erst in 300 ms, damit eine optionale
 * abweichende Fehlerbehandlung vom aufrufenden Code angehängt werden kann
 * @returns {Promise}
 */
export function defaultCatchAsError<T>(promise: Promise<T>): Promise<T> {
  _.delay(() => promise.catch(serverCommunicationError), 300);
  return promise;
}

/**
 * Fügt ein catch zu einem Promise hinzu - aber erst in 300 ms, damit eine optionale
 * abweichende Fehlerbehandlung vom aufrufenden Code angehängt werden kann
 * @returns {Promise}
 */
export function defaultCatchAsWarning<T>(promise: Promise<T>): Promise<T> {
  _.delay(() => promise.catch(serverCommunicationWarning), 300);
  return promise;
}

/**
 * Zeigt eine Standard-Fehlermeldung für schwerwiegende Bugs an
 * @param message
 */
function showInternalError(message: string | null): void {
  let errorMessage =
    '<h3>Tut uns leid, aber es ist ein Fehler aufgetreten. <br/> Sie müssen die Seite neu laden </h3> <br/> <span style="font-weight:normal;font-size:small;"> Details: %s </span>';
  try {
    errorMessage = I18N("ErrorOccuredWithMessage", message);
  } catch (e) {}
  showError(errorMessage);
}

/**
 * Zeigt eine Standard-Fehlermeldung für nicht so schlimme Bugs an
 * @param message
 */
function showInternalWarning(message: string | null): void {
  let warningMessage =
    '<h3>Tut uns leid, aber es ist ein Fehler aufgetreten</h3> <br/> <span style="font-weight:normal;font-size:small;"> Details: %s </span>';
  try {
    warningMessage = I18N("WarningOccuredWithMessage", message);
  } catch (e) {}
  showWarning(warningMessage);
}
